/* eslint @typescript-eslint/strict-boolean-expressions: off */

import flatten from "lodash/flatten";
import mapValues from "lodash/mapValues";
import sortBy from "lodash/sortBy";
import sum from "lodash/sum";
import uniqBy from "lodash/uniqBy";
import values from "lodash/values";
import { ChartColumns } from "src/components/page-components/QueryPage/visuals-options";
import { ChartType, ColumnMapping } from "src/gql/visuals/types";
import { QueryResult } from "src/utils/results/types";
import { QueryResultColumn } from "src/utils/results/types";
import { QueryResultRow } from "src/utils/results/types";
import { VisualChart } from "src/gql/visuals/types";
import {
  chartOptionBubbleChart,
  chartOptionPercentages,
} from "src/components/chart/options";
import { createChartColumns } from "src/components/page-components/QueryPage/visuals-options";
import { guessColumnType } from "src/utils/results/values";
import { isNonNullable } from "src/utils/types/types";
import { isNumber } from "src/utils/types/types";
import { resultPrimitive } from "src/utils/results/values";
import { rowValues } from "src/components/chart/scales";

export interface ChartSeries {
  rows: QueryResultRow[];
  x: QueryResultColumn;
  z: QueryResultColumn;
  ys: QueryResultColumn[];
  y0: ChartSeriesTypes;
  y1: ChartSeriesTypes;
}

export interface ChartSeriesTypes {
  column: QueryResultColumn[];
  area: QueryResultColumn[];
  scatter: QueryResultColumn[];
  line: QueryResultColumn[];
  pie: QueryResultColumn[];
}

export const createChartSeries = (
  visual: VisualChart,
  result: QueryResult
): ChartSeries => {
  const columns = createChartColumns(visual, result);
  let series = createChartSeriesGroups(visual, result, columns);
  const y0s = leftAxisColumns(series);
  const y1s = rightAxisColumns(series);

  if (!columns.x) {
    throw new Error("Missing x-axis.");
  }

  if (columns.ys.length === 0) {
    throw new Error("Missing y-axis.");
  }

  if (!series.rows.length) {
    throw new Error("Missing result data.");
  }

  if (uniqBy(y0s, "type").length > 1 || uniqBy(y1s, "type").length > 1) {
    throw new Error("All columns for a y-axis must have the same data type.");
  }

  if (chartOptionPercentages(visual) && y0s.length > 0 && y1s.length > 0) {
    throw new Error("Normalization may only be used with a single y-axis.");
  }

  if (series.y0.column.length > 0 && series.y1.column.length > 0) {
    throw new Error("All column series must use the same y-axis.");
  }

  if (series.y0.pie.length + series.y1.pie.length > 1) {
    throw new Error("Only one pie chart at a time please.");
  }

  if (chartOptionPercentages(visual)) {
    series = normalizePercentages(visual, series, y0s);
    series = normalizePercentages(visual, series, y1s);
  }

  return series;
};

// Create the ChartSeries object while sorting and grouping rows and columns.
const createChartSeriesGroups = (
  visual: VisualChart,
  result: QueryResult,
  columns: ChartColumns
): ChartSeries => {
  const rows = result.matrix?.rows ?? [];
  let ys = sortColumns(visual, columns.ys);

  if (!columns.group) {
    return {
      x: columns.x!,
      z: columns.z!,
      ys,
      y0: groupTypes(visual, filterY0(visual, ys)),
      y1: groupTypes(visual, filterY1(visual, ys)),
      rows: sortRows(visual, columns, mergePieRows(visual, columns, rows)),
    };
  }

  // If the chart options are configured to use a group_by column,
  // we transform the chart data once here, so that the rest of the
  // chart code can deal with rows and columns in a normalized form.
  const groups: { [key: string]: QueryResultColumn } = {};
  const seen: { [key: string]: number } = {};
  const grouped: QueryResultRow[] = [];
  rows.forEach((row) => {
    const x = resultPrimitive(row[columns.x?.name ?? ""]);
    const y = resultPrimitive(row[columns.ys[0]?.name ?? ""]);
    const z = resultPrimitive(row[columns.z?.name ?? ""]);
    const g = resultPrimitive(row[columns.group?.name ?? ""]);

    if (isNonNullable(x) && isNonNullable(y) && isNonNullable(g)) {
      if (x in seen) {
        grouped[seen[x]][g] = y;
      } else {
        grouped.push({
          [columns.x!.name]: x,
          [g]: y,
          ...(isNonNullable(z) ? { [columns.z!.name]: z } : {}),
        });
        // Don't group with z axis, otherwise it would be overwritten
        if (!chartOptionBubbleChart(visual, columns.z)) {
          seen[x] = grouped.length - 1;
        }
      }
      if (!groups[String(g)]) {
        groups[String(g)] = {
          name: String(g),
          type: guessColumnType(y),
        };
      }
    }
  });

  ys = sortColumns(visual, Object.values(groups));

  return {
    x: columns.x!,
    z: columns.z!,
    ys,
    y0: groupTypes(visual, filterY0(visual, ys)),
    y1: groupTypes(visual, filterY1(visual, ys)),
    rows: sortRows(visual, columns, mergePieRows(visual, columns, grouped)),
  };
};

// Group chart columns by their chart type.
const groupTypes = (
  visual: VisualChart,
  columns: QueryResultColumn[]
): ChartSeriesTypes => {
  return {
    column: columns.filter((c) => columnType(visual, c) === "column"),
    area: columns.filter((c) => columnType(visual, c) === "area"),
    scatter: columns.filter((c) => columnType(visual, c) === "scatter"),
    line: columns.filter((c) => columnType(visual, c) === "line"),
    pie: columns.filter((c) => columnType(visual, c) === "pie"),
  };
};

// Get the chart type for a column (either the gloabal type or an override).
const columnType = (
  visual: VisualChart,
  column: QueryResultColumn
): ChartType => {
  return (
    visual.options.seriesOptions?.[column.name]?.type ||
    visual.options.globalSeriesType
  );
};

// Sort columns by their user-defined z-index.
const sortColumns = (visual: VisualChart, columns: QueryResultColumn[]) => {
  return sortBy(columns, (c) => {
    return visual.options.seriesOptions?.[c.name]?.zIndex ?? 0;
  });
};

// For pie charts, merge rows so that all rows have a unique x-value.
// This merging is required to match the earlier behaviour in Redash.
const mergePieRows = (
  visual: VisualChart,
  columns: ChartColumns,
  rows: QueryResultRow[]
) => {
  if (!columns.x || visual.options.globalSeriesType !== "pie") {
    if (!columns.x) {
      return rows;
    } else {
      // Filter out null values from the x-axis
      return rows.filter((r) => r[columns.x!.name] !== null);
    }
  }

  const seen = new Map<any, number>();
  const merged: QueryResultRow[] = [];
  const x = columns.x.name;

  rows.forEach((row) => {
    const index = seen.get(row[x]);
    if (typeof index === "number") {
      columns.ys.forEach((y) => {
        const prev = merged[index][y.name];
        const next = row[y.name];
        if (typeof prev === "number" && typeof next === "number") {
          merged[index][y.name] = prev + next;
        } else {
          merged[index][y.name] = next;
        }
      });
    } else {
      const length = merged.push({ ...row });
      seen.set(row[x], length - 1);
    }
  });

  return merged;
};

// Sort rows according to the sortX visualization options.
const sortRows = (
  visual: VisualChart,
  columns: ChartColumns,
  rows: QueryResultRow[]
) => {
  if (!columns.x) {
    return rows;
  } else if (visual.options.sortX === false) {
    return rows;
  } else {
    return sortBy(rows, columns.x.name);
  }
};

// Filter columns for the left y-axis (which is the default axis).
const filterY0 = (visual: VisualChart, columns: QueryResultColumn[]) => {
  return columns.filter((c) => {
    return visual.options.seriesOptions?.[c.name]?.yAxis !== 1;
  });
};

// Filter columns for the right y-axis (specifically set by the user).
const filterY1 = (visual: VisualChart, columns: QueryResultColumn[]) => {
  return columns.filter((c) => {
    return visual.options.seriesOptions?.[c.name]?.yAxis === 1;
  });
};

// Get a list of all columns for either y-axis.
export const yAxisColumns = (series: ChartSeries): QueryResultColumn[] => {
  return [...leftAxisColumns(series), ...rightAxisColumns(series)];
};

// Get a list of all columns for the left y-axis.
export const leftAxisColumns = (series: ChartSeries): QueryResultColumn[] => {
  return flatten(values(series.y0));
};

// Get a list of all columns for the right y-axis.
export const rightAxisColumns = (series: ChartSeries): QueryResultColumn[] => {
  return flatten(values(series.y1));
};

// columnMapping configuration can contain outdated configuration options, so we have to
// check if the column exists first.
// Issue: https://dune.height.app/app-team-tech-improvements/T-17126
export const getGroupByYColumnName = (
  columnMapping: ColumnMapping | undefined,
  columns: QueryResultColumn[] | undefined
) => {
  if (columnMapping && columns) {
    // if results are grouped return y column name
    if (columns.find(({ name }) => columnMapping[name] === "series")) {
      return columns.find(({ name }) => columnMapping[name] === "y")?.name;
    }
  }
};

const normalizePercentages = (
  _visual: VisualChart, // Unused
  series: ChartSeries,
  ys: QueryResultColumn[]
): ChartSeries => {
  const names = ys.map((y) => y.name);

  const rows = series.rows.map((row) => {
    const total = sum(rowValues(row, ys).filter(isNumber));
    return mapValues(row, (value, y) => {
      return names.includes(y) && typeof value === "number"
        ? value / total
        : value;
    });
  });

  return {
    ...series,
    rows,
  };
};
